import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  Image,
  Platform
} from 'react-native';
import { observer } from '@legendapp/state/react';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import theme from '../../../../config/theme';
import { observable } from '@legendapp/state';
import { databaseService } from '../../../../services/databaseService';
import { APP_CONSTANTS } from '../../../../config/constants';
import * as ImagePicker from 'expo-image-picker';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { uploadUserProfileImage, getUserProfile, uploadCompanyUserProfileImage } from '../../../../services/userService';
import { useWebAlert } from '../../../../components/WebAlert';
import { supabase } from '../../../../services/supabase';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FormattedInput from '../../../../components/FormattedInput';
import FormFieldError from '../../../../components/FormFieldError';
import { globalState } from '../../../../state/globalState';
import { pickImageFromLibrary, takePhotoFromCamera } from '@/utils/media/imagePicker';
import {
  getActionSheetContainerPaddingBottom,
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import {
  getCompanyManagedUserTypeOptions,
  getUserTypeLabel,
  isAdminUserType,
  isDeveloperUserType,
  isMotoristaUserType,
  normalizeUserType,
} from '@/utils/userRoles';

// Interface para dados de usuário
interface User {
  id: string;
  name: string;
  email: string;
  telefone?: string;
  empresa_id?: string | null;
  photo_url?: string;
  photo_local_path?: string;
  user_type: string;
  created_at: string;
}

// Estado local da UI para esta página
export const userDetailsUIState = observable({
  isLoading: true,
  isSaving: false,
  editField: null as string | null,
  user: null as User | null,
  editData: {
    name: '',
    telefone: '',
    user_type: ''
  }
});

// Ações para manipular o estado da UI
export const userDetailsUIActions = {
  setLoading: (v: boolean) => userDetailsUIState.isLoading.set(v),
  setSaving: (v: boolean) => userDetailsUIState.isSaving.set(v),
  setEditField: (field: string | null) => userDetailsUIState.editField.set(field),
  setUser: (user: User | null) => userDetailsUIState.user.set(user),
  updateEditData: (data: any) => {
    userDetailsUIState.editData.set({
      ...userDetailsUIState.editData.get(),
      ...data
    });
  },
  initFromUser: (user: User | null) => {
    if (user) {
      const normalizedUserType = normalizeUserType(user.user_type);
      userDetailsUIState.editData.set({
        name: user.name || '',
        telefone: user.telefone || '',
        user_type:
          normalizedUserType === 'admin'
            ? 'admin'
            : normalizedUserType === 'motorista'
              ? 'motorista'
              : user.user_type || 'motorista'
      });
    }
  }
};

// Interface para o componente de campo editável
interface EditableFieldProps {
  label: string;
  value: string;
  fieldName: string;
  editable?: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onChangeText: (text: string) => void;
  placeholder?: string;
  helpText?: string;
  keyboardType?: any;
}

// Componente de campo editável
const EditableField = observer(({
  label,
  value,
  fieldName,
  editable = true,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onChangeText,
  placeholder,
  helpText,
  keyboardType = 'default'
}: EditableFieldProps) => {
  const currentEditField = userDetailsUIState.editField.get();
  const isCurrentlyEditing = currentEditField === fieldName;
  const editData = userDetailsUIState.editData.get();
  const editValue = editData[fieldName as keyof typeof editData];
  const isSaving = userDetailsUIState.isSaving.get();

  return (
    <View style={styles.fieldRow}>
      <View style={styles.fieldLabelContainer}>
        <Text style={styles.fieldLabel}>{label}</Text>
      </View>

      <View style={styles.fieldValueContainer}>
        {isCurrentlyEditing ? (
          <View style={styles.editContainer}>
            <TextInput
              style={styles.input}
              value={editValue as string}
              onChangeText={onChangeText}
              placeholder={placeholder}
              placeholderTextColor={theme.colors.text.tertiary}
              keyboardType={keyboardType}
              autoFocus
            />
            <View style={styles.editActions}>
              <TouchableOpacity
                style={[styles.editButton, styles.cancelButton]}
                onPress={onCancel}
                disabled={isSaving}
              >
                <Text style={styles.editButtonText}>Cancelar</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.editButton}
                onPress={onSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                ) : (
                  <Text style={styles.editButtonText}>Salvar</Text>
                )}
              </TouchableOpacity>
            </View>

            {helpText && (
              <Text style={styles.helpText}>{helpText}</Text>
            )}
          </View>
        ) : (
          <View style={styles.displayContainer}>
            <Text style={styles.fieldValue}>{value || 'Não informado'}</Text>

            {editable && (
              <TouchableOpacity
                style={styles.editIconButton}
                onPress={onEdit}
              >
                <MaterialCommunityIcons name="pencil" size={24} color={theme.colors.primary.default} />
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    </View>
  );
});

// Componente para campo de seleção de tipo de usuário
const UserTypeField = observer(() => {
  const currentEditField = userDetailsUIState.editField.get();
  const isCurrentlyEditing = currentEditField === 'user_type';
  const editData = userDetailsUIState.editData.get();
  const user = userDetailsUIState.user.get();
  const isSaving = userDetailsUIState.isSaving.get();
  const { showAlert } = useWebAlert();
  
  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string, onOk?: () => void) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK', onPress: onOk }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg, [{ text: 'OK', onPress: onOk }]);
    }
  };

  const userTypeOptions = getCompanyManagedUserTypeOptions();

  const handleEdit = () => {
    userDetailsUIActions.setEditField('user_type');
  };

  const handleSave = () => {
    userDetailsUIActions.setSaving(true);
    setTimeout(() => {
      handleSaveField('user_type', showMessage);
    }, 100);
  };

  const handleCancel = () => {
    userDetailsUIActions.setEditField(null);
    if (user) {
      const normalizedUserType = normalizeUserType(user.user_type);
      userDetailsUIActions.updateEditData({
        user_type:
          normalizedUserType === 'admin'
            ? 'admin'
            : normalizedUserType === 'motorista'
              ? 'motorista'
              : user.user_type,
      });
    }
  };

  return (
    <View style={styles.fieldRow}>
      <View style={styles.fieldLabelContainer}>
        <Text style={styles.fieldLabel}>Tipo</Text>
      </View>

      <View style={styles.fieldValueContainer}>
        {isCurrentlyEditing ? (
          <View style={styles.editContainer}>
            {userTypeOptions.map((option, index) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.userTypeOption,
                  editData.user_type === option.value && styles.userTypeOptionSelected
                ]}
                onPress={() => userDetailsUIActions.updateEditData({ user_type: option.value })}
              >
                <Text
                  style={[
                    styles.userTypeOptionText,
                    editData.user_type === option.value && styles.userTypeOptionTextSelected
                  ]}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}

            <View style={styles.editActions}>
              <TouchableOpacity
                style={[styles.editButton, styles.cancelButton]}
                onPress={handleCancel}
                disabled={isSaving}
              >
                <Text style={styles.editButtonText}>Cancelar</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.editButton}
                onPress={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                ) : (
                  <Text style={styles.editButtonText}>Salvar</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        ) : (
          <View style={styles.displayContainer}>
            <View style={styles.userTypeContainer}>
              <Text style={[
                styles.userTypeTag,
                isAdminUserType(user?.user_type) && styles.userTypeTagAdmin,
                isMotoristaUserType(user?.user_type) && styles.userTypeTagGestor
              ]}>
                {user ? getUserTypeLabel(user.user_type) : ''}
              </Text>
            </View>

            <TouchableOpacity
              style={styles.editIconButton}
              onPress={handleEdit}
            >
              <MaterialCommunityIcons name="pencil" size={24} color={theme.colors.primary.default} />
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );
});

// Componente para a foto do usuário
const UserAvatarField = observer(() => {
  const user = userDetailsUIState.user.get();
  const [uploading, setUploading] = useState(false);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const { showActionSheetWithOptions } = useActionSheet();
  const { showAlert } = useWebAlert();
  const safeAreaInsets = useSafeAreaInsets();
  
  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string, onOk?: () => void) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK', onPress: onOk }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg, [{ text: 'OK', onPress: onOk }]);
    }
  };

  // Determinar a URL da foto
  const photoUrl = previewUri || (user?.photo_url || user?.photo_local_path || null);

  const handlePickImage = () => {
    showActionSheetWithOptions(
      {
        options: ['Cancelar', 'Tirar foto', 'Galeria'],
        cancelButtonIndex: 0,
        title: 'Selecionar imagem',
        message: 'De onde você quer obter a foto do usuário?',
        // Estilos baseados no tema da aplicação
        titleTextStyle: {
          color: theme.colors.text.primary,
          fontSize: theme.fontSizes.md,
          fontWeight: theme.fontWeights.medium as any,
        },
        messageTextStyle: {
          color: theme.colors.text.secondary,
          fontSize: theme.fontSizes.sm,
        },
        containerStyle: {
          backgroundColor: theme.colors.background.primary,
          paddingBottom: getActionSheetContainerPaddingBottom(safeAreaInsets.bottom),
        },
        textStyle: {
          color: theme.colors.primary.default,
          fontSize: theme.fontSizes.md,
        },
      },
      async (buttonIndex) => {
        if (buttonIndex === 1) {
          // câmera
          await takePhoto();
        }
        if (buttonIndex === 2) {
          // galeria
          await pickFromLibrary();
        }
      }
    );
  };

  const takePhoto = async () => {
    try {
      const outcome = await takePhotoFromCamera({
        mediaTypes: ["images"],
        allowsEditing: false,
        aspect: [1, 1],
        quality: 0.7,
      }, {
        source: 'settings_user_details_photo',
        screen: 'settings/users/[id]',
        action: 'take_company_user_photo_with_camera',
        entityType: 'company_user_profile_photo',
        entityId: user?.id ?? null,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar a câmera.');
        return;
      }

      if (outcome.status === 'failed') {
        console.error('Erro ao tirar foto:', outcome.error);
        showMessage('Erro', 'Não foi possível tirar a foto.');
        return;
      }

      if (outcome.status === 'success' && outcome.result.assets?.length > 0) {
        const selectedImage = outcome.result.assets[0];
        setPreviewUri(selectedImage.uri);
        handleSelectedImage(selectedImage);
      }
    } catch (error) {
      console.error('Erro ao tirar foto:', error);
      showMessage('Erro', 'Não foi possível tirar a foto.');
    }
  };

  const pickFromLibrary = async () => {
    try {
      const outcome = await pickImageFromLibrary({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        aspect: [1, 1],
        quality: 0.7,
      }, {
        source: 'settings_user_details_photo',
        screen: 'settings/users/[id]',
        action: 'pick_company_user_photo_from_library',
        entityType: 'company_user_profile_photo',
        entityId: user?.id ?? null,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar suas fotos.');
        return;
      }

      if (outcome.status === 'failed') {
        console.error('Erro ao abrir a galeria:', outcome.error);
        showMessage('Erro', 'Não foi possível selecionar a imagem.');
        return;
      }

      if (outcome.status !== 'success' || !outcome.result.assets?.length) {
        return;
      }

      const selectedImage = outcome.result.assets[0];
      setPreviewUri(selectedImage.uri);
      handleSelectedImage(selectedImage);
    } catch (error) {
      console.error('Erro ao selecionar imagem da galeria:', error);
      showMessage('Erro', 'Não foi possível selecionar a imagem.');
    }
  };

  const handleSelectedImage = async (asset: ImagePicker.ImagePickerAsset) => {
    if (!user?.id) return;
    const userId = user.id;

    setUploading(true);

    // Atualizar apenas o preview local
    setPreviewUri(asset.uri);

    try {
      // Usar a nova função específica para usuários da empresa
      const result = await uploadCompanyUserProfileImage(userId, asset.uri);
      if (!result.success) {
        throw new Error(result.error);
      }
      
      // Se teve sucesso, o resultado já contém o usuário atualizado
      if (result.data && result.data.user) {
        const updatedUser = result.data.user as User;
        userDetailsUIActions.setUser(updatedUser);
      } else {
        // Caso não tenha o usuário no resultado, buscar do banco
        const updatedUserData = await databaseService.readItem<User>(
          APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
          userId
        );
        
        if (updatedUserData) {
          userDetailsUIActions.setUser(updatedUserData);
        }
      }
      
      showMessage('Sucesso', 'Foto atualizada com sucesso!');
    } catch (err) {
      console.warn('Falha no upload:', err);
      // Se falhou no upload, ainda salvamos o caminho local da imagem para mostrar ao usuário
      const updatedUser = { ...user, photo_local_path: asset.uri };
      userDetailsUIActions.setUser(updatedUser);
      
      showMessage('Aviso', 'Imagem salva localmente e será enviada quando estiver online.');
    } finally {
      setUploading(false);
      // Limpa preview em qualquer caso (se falhou, photo_local_path já cobre)
      setPreviewUri(null);
    }
  };

  return (
    <View style={styles.fieldRow}>
      <View style={styles.fieldLabelContainer}>
        <Text style={styles.fieldLabel}>Foto</Text>
      </View>

      <View style={styles.fieldValueContainer}>
        <View style={styles.avatarContainer}>
          {photoUrl ? (
            <Image
              source={{ uri: photoUrl }}
              style={styles.avatar}
            />
          ) : (
            <View style={[styles.avatar, styles.avatarPlaceholder]}>
              <MaterialCommunityIcons name="account" size={40} color={theme.colors.text.inverse} />
            </View>
          )}

          <TouchableOpacity
            style={styles.changePhotoButton}
            onPress={handlePickImage}
            disabled={uploading}
          >
            {uploading ? (
              <ActivityIndicator size="small" color={theme.colors.primary.default} />
            ) : (
              <Text style={styles.changePhotoText}>Alterar foto</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
});

// Função auxiliar para salvar um campo específico
const handleSaveField = async (fieldName: string, showMessageFn: (title: string, msg: string, onOk?: () => void) => void) => {
  try {
    const editData = userDetailsUIState.editData.get();
    const user = userDetailsUIState.user.get();

    if (!user?.id) {
      showMessageFn('Erro', 'Não foi possível identificar o usuário.');
      userDetailsUIActions.setSaving(false);
      return;
    }

    // Objeto com apenas o campo que está sendo editado
    const updateData: any = {};
    updateData[fieldName] = editData[fieldName as keyof typeof editData];

    if (fieldName === 'user_type') {
      const normalizedUserType = normalizeUserType(String(updateData.user_type || ''));
      if (normalizedUserType !== 'motorista' && normalizedUserType !== 'admin') {
        showMessageFn('Erro', 'Tipo de usuário inválido para gestão da empresa.');
        userDetailsUIActions.setSaving(false);
        return;
      }
      updateData.user_type = normalizedUserType;
    }

    userDetailsUIActions.setSaving(true);

    try {
      // Atualizar no banco local
      const success = await databaseService.updateItem(
        APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
        user.id,
        updateData
      );

      if (!success) {
        throw new Error('Falha ao atualizar dados localmente');
      }

      // Atualizar o estado local do usuário
      userDetailsUIActions.setUser({
        ...user,
        ...updateData
      });

      // Atualização local feita com sucesso
      showMessageFn('Sucesso', 'Dados atualizados com sucesso!');
      userDetailsUIActions.setEditField(null);

    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      showMessageFn('Erro', 'Não foi possível atualizar os dados. Tente novamente.');
    } finally {
      userDetailsUIActions.setSaving(false);
    }
  } catch (error) {
    console.error('Exceção ao atualizar usuário:', error);
    showMessageFn('Erro', 'Ocorreu um erro ao atualizar os dados.');
    userDetailsUIActions.setSaving(false);
  }
};

// Página principal de detalhes de usuário
const UserDetailsScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const { id } = useLocalSearchParams();
  const userId = Array.isArray(id) ? id[0] : id;
  const { showAlert } = useWebAlert();
  const currentEmpresaId = globalState.user.profile.get()?.empresa_id || null;
  
  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string, onOk?: () => void) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK', onPress: onOk }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg, [{ text: 'OK', onPress: onOk }]);
    }
  };
  
  // Buscar dados do usuário ao carregar a página
  useEffect(() => {
    const loadUserData = async () => {
      if (!userId) {
        showMessage('Erro', 'ID de usuário não fornecido.');
        router.back();
        return;
      }

      userDetailsUIActions.setLoading(true);

      try {
        // Buscar usuário no banco de dados local
        const userData = await databaseService.readItem<User>(
          APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
          userId as string
        );

        if (userData) {
          if (isDeveloperUserType(userData.user_type)) {
            showMessage(
              'Acesso restrito',
              'Perfis de desenvolvedor não são gerenciados pela tela da empresa.',
              () => router.replace('/settings/users')
            );
            return;
          }

          if (currentEmpresaId && userData.empresa_id && userData.empresa_id !== currentEmpresaId) {
            showMessage(
              'Acesso restrito',
              'Este usuário não pertence à empresa do administrador atual.',
              () => router.replace('/settings/users')
            );
            return;
          }

          userDetailsUIActions.setUser(userData);
          userDetailsUIActions.initFromUser(userData);
        } else {
          showMessage('Erro', 'Usuário não encontrado.');
          router.back();
          return;
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
        showMessage('Erro', 'Não foi possível carregar os dados do usuário.');
        router.back();
      } finally {
        userDetailsUIActions.setLoading(false);
      }
    };

    loadUserData();

    // Cleanup ao desmontar o componente
    return () => {
      userDetailsUIActions.setUser(null);
      userDetailsUIActions.setEditField(null);
    };
  }, [currentEmpresaId, userId, router, showAlert]);

  // Funções para lidar com a edição de campos
  const handleEditField = (fieldName: string) => {
    userDetailsUIActions.setEditField(fieldName);
  };

  const handleCancelEdit = () => {
    userDetailsUIActions.setEditField(null);
    // Restaurar valores originais
    const user = userDetailsUIState.user.get();
    if (user) {
      userDetailsUIActions.initFromUser(user);
    }
  };

  const handleSave = (fieldName: string) => {
    userDetailsUIActions.setSaving(true);
    setTimeout(() => {
      handleSaveField(fieldName, showMessage);
    }, 100);
  };

  const handleGoBack = () => {
    router.back();
  };

  const user = userDetailsUIState.user.get();
  const isLoading = userDetailsUIState.isLoading.get();

  // Estado para redefinição de senha
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validação dos campos
  const validatePassword = (password: string): boolean => {
    if (!password) {
      setNewPasswordError('Campo obrigatório');
      return false;
    } else if (password.length < 6) {
      setNewPasswordError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }
    setNewPasswordError(null);
    return true;
  };
  const validateConfirmPassword = (password: string, confirm: string): boolean => {
    if (!confirm) {
      setConfirmPasswordError('Campo obrigatório');
      return false;
    } else if (password !== confirm) {
      setConfirmPasswordError('As senhas não coincidem');
      return false;
    }
    setConfirmPasswordError(null);
    return true;
  };
  const validateForm = (): boolean => {
    const validPass = validatePassword(newPassword);
    const validConf = validateConfirmPassword(newPassword, confirmPassword);
    return validPass && validConf;
  };

  // Handler para redefinir senha
  const handleResetPassword = async () => {
    setFormError(null);
    if (!validateForm()) return;
    if (!user) return;

    const confirmed = await new Promise<boolean>(resolve => {
      showAlert(
        'Confirmar redefinição de senha',
        `Você vai redefinir a senha de ${user.name} (${user.email}). Deseja continuar?`,
        [
          { text: 'Cancelar', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Redefinir senha', onPress: () => resolve(true) }
        ]
      );
    });
    if (!confirmed) return;

    setResetLoading(true);
    let customError: { message?: string; code?: string } | null = null;
    try {
      const { data, error } = await supabase.functions.invoke('admin-update-user-password', {
        method: 'PUT',
        body: { id: user.id, new_password: newPassword }
      });
      if (error) {
        if (error instanceof FunctionsHttpError) {
          try {
            customError = await error.context.json();
          } catch (e) {}
        }
        let msg = customError?.message || error.message || 'Erro ao redefinir senha. Por favor, tente novamente mais tarde.';
        setFormError(msg);
        setResetLoading(false);
        return;
      }
      setResetSuccess(true);
      showMessage('Sucesso', 'Senha redefinida com sucesso!');
      setShowResetPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      let msg = err?.message || 'Erro inesperado ao redefinir senha.';
      setFormError(msg);
    } finally {
      setResetLoading(false);
    }
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary.default} />
        <Text style={styles.loadingText}>Carregando informações do usuário...</Text>
      </View>
    );
  }

  if (!user) {
    return (
      <View style={styles.errorContainer}>
        <MaterialCommunityIcons name="alert-circle-outline" size={60} color={theme.colors.util.error} />
        <Text style={styles.errorText}>Não foi possível carregar o usuário.</Text>
        <TouchableOpacity style={styles.backToListButton} onPress={handleGoBack}>
          <Text style={styles.backToListButtonText}>Voltar para a lista</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={handleGoBack}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Detalhes do Usuário</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.section}>
          <UserAvatarField />

          <EditableField
            label="Nome"
            value={user.name}
            fieldName="name"
            isEditing={true}
            onEdit={() => handleEditField('name')}
            onSave={() => handleSave('name')}
            onCancel={handleCancelEdit}
            onChangeText={(text) => userDetailsUIActions.updateEditData({ name: text })}
            placeholder="Nome do usuário"
          />

          <EditableField
            label="E-mail"
            value={user.email}
            fieldName="email"
            editable={false}
            isEditing={false}
            onEdit={() => {}}
            onSave={() => {}}
            onCancel={() => {}}
            onChangeText={() => {}}
            helpText="O e-mail não pode ser alterado"
          />

          <EditableField
            label="Telefone"
            value={user.telefone || ''}
            fieldName="telefone"
            isEditing={true}
            onEdit={() => handleEditField('telefone')}
            onSave={() => handleSave('telefone')}
            onCancel={handleCancelEdit}
            onChangeText={(text) => userDetailsUIActions.updateEditData({ telefone: text })}
            placeholder="Telefone de contato"
            keyboardType="phone-pad"
          />

          <UserTypeField />

          <View style={styles.metadataRow}>
            <Text style={styles.metadataLabel}>Cadastrado em:</Text>
            <Text style={styles.metadataValue}>
              {new Date(user.created_at).toLocaleDateString('pt-BR')}
            </Text>
          </View>
        </View>

        <View style={styles.actionsSection}>
          {/* Redefinir senha */}
          {showResetPassword ? (
            <View style={{ marginBottom: 16 }}>
              {/* Nova senha */}
              <View style={styles.inputContainer}>
                <MaterialCommunityIcons
                  name="lock-outline"
                  size={20}
                  color={newPasswordError ? theme.colors.util.error : theme.colors.text.secondary}
                  style={styles.inputIcon}
                />
                <TextInput
                  style={[
                    styles.input,
                    newPasswordError ? styles.inputError : null,
                    { flex: 1 }
                  ]}
                  value={newPassword}
                  onChangeText={text => {
                    setNewPassword(text);
                    setFormError(null);
                    validatePassword(text);
                    validateConfirmPassword(text, confirmPassword);
                  }}
                  placeholder="Nova senha"
                  placeholderTextColor={theme.colors.text.tertiary}
                  secureTextEntry={!showNewPassword}
                  maxLength={32}
                />
                <TouchableOpacity
                  style={styles.eyeIcon}
                  onPress={() => setShowNewPassword(!showNewPassword)}
                >
                  <MaterialCommunityIcons
                    name={showNewPassword ? "eye-off-outline" : "eye-outline"}
                    size={22}
                    color={theme.colors.text.secondary}
                  />
                </TouchableOpacity>
              </View>
              <FormFieldError message={newPasswordError} />
              {/* Confirmar nova senha */}
              <View style={[styles.inputContainer, { marginTop: theme.spacing.md }]}>
                <MaterialCommunityIcons
                  name="lock-outline"
                  size={20}
                  color={confirmPasswordError ? theme.colors.util.error : theme.colors.text.secondary}
                  style={styles.inputIcon}
                />
                <TextInput
                  style={[
                    styles.input,
                    confirmPasswordError ? styles.inputError : null,
                    { flex: 1 }
                  ]}
                  value={confirmPassword}
                  onChangeText={text => {
                    setConfirmPassword(text);
                    setFormError(null);
                    validateConfirmPassword(newPassword, text);
                  }}
                  placeholder="Confirmar nova senha"
                  placeholderTextColor={theme.colors.text.tertiary}
                  secureTextEntry={!showConfirmPassword}
                  maxLength={32}
                />
                <TouchableOpacity
                  style={styles.eyeIcon}
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <MaterialCommunityIcons
                    name={showConfirmPassword ? "eye-off-outline" : "eye-outline"}
                    size={22}
                    color={theme.colors.text.secondary}
                  />
                </TouchableOpacity>
              </View>
              <FormFieldError message={confirmPasswordError} />
              {formError && <FormFieldError message={formError} />}
              <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 8 }}>
                <TouchableOpacity
                  style={[styles.editButton, styles.cancelButton]}
                  onPress={() => {
                    setShowResetPassword(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setFormError(null);
                    setNewPasswordError(null);
                    setConfirmPasswordError(null);
                  }}
                  disabled={resetLoading}
                >
                  <Text style={styles.editButtonText}>Cancelar</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.editButton}
                  onPress={handleResetPassword}
                  disabled={resetLoading}
                >
                  <Text style={styles.editButtonText}>{resetLoading ? 'Redefinindo...' : 'Redefinir senha'}</Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <TouchableOpacity style={styles.actionButton} onPress={() => setShowResetPassword(true)}>
              <Text style={styles.actionButtonText}>Redefinir senha</Text>
            </TouchableOpacity>
          )}
          
          <TouchableOpacity
            style={[styles.actionButton, styles.dangerButton]}
            onPress={async () => {
              if (!user) return;
              const confirm = await new Promise<boolean>(resolve => {
                showAlert(
                  'Confirmar exclusão do usuário',
                  `Você vai excluir permanentemente ${user.name} (${user.email}). Esta ação não pode ser desfeita. Deseja continuar?`,
                  [
                    { text: 'Cancelar', style: 'cancel', onPress: () => resolve(false) },
                    { text: 'Excluir usuário', style: 'destructive', onPress: () => resolve(true) }
                  ]
                );
              });
              if (!confirm) return;
              try {
                userDetailsUIActions.setSaving(true);
                let customError: { message?: string; code?: string } | null = null;
                // Chamar edge function
                const { data, error } = await supabase.functions.invoke('admin-delete-user-same-company', {
                  method: 'DELETE',
                  body: { id: user.id }
                });
                if (error) {
                  if (error instanceof FunctionsHttpError) {
                    try {
                      customError = await error.context.json();
                    } catch (e) {}
                  }
                  let msg = customError?.message || error.message || 'Erro ao desativar usuário. Por favor, tente novamente mais tarde.';
                  showMessage('Erro', msg);
                  userDetailsUIActions.setSaving(false);
                  return;
                }
                showMessage('Sucesso', 'Usuário desativado (deletado) com sucesso!', () => router.back());
              } catch (err: any) {
                let msg = err?.message || 'Erro inesperado ao desativar usuário.';
                showMessage('Erro', msg);
              } finally {
                userDetailsUIActions.setSaving(false);
              }
            }}
            disabled={userDetailsUIState.isSaving.get()}
          >
            <Text style={styles.dangerButtonText}>Desativar usuário</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    padding: theme.spacing.lg,
  },
  errorText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.lg,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    marginBottom: theme.spacing.lg,
  },
  backToListButton: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    minWidth: 150,
    alignItems: 'center',
  },
  backToListButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  headerRight: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: theme.spacing.xl,
  },
  section: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    ...theme.shadows.md,
    overflow: 'hidden',
  },
  actionsSection: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    ...theme.shadows.md,
    overflow: 'hidden',
    padding: theme.spacing.lg,
  },
  actionButton: {
    backgroundColor: theme.colors.background.secondary,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    marginBottom: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border.default,
  },
  actionButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  dangerButton: {
    backgroundColor: `${theme.colors.util.error}10`,
    borderColor: theme.colors.util.error,
  },
  dangerButtonText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  fieldRow: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  metadataRow: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
    justifyContent: 'space-between',
  },
  metadataLabel: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    fontWeight: theme.fontWeights.medium as any,
  },
  metadataValue: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
  fieldLabelContainer: {
    width: 100,
    justifyContent: 'center',
  },
  fieldLabel: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.secondary,
  },
  fieldValueContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  displayContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fieldValue: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    flex: 1,
  },
  editIconButton: {
    padding: theme.spacing.xs,
  },
  editContainer: {
    width: '100%',
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
  },
  editActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: theme.spacing.sm,
  },
  editButton: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    marginLeft: theme.spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: theme.colors.text.tertiary,
  },
  editButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  helpText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
    marginTop: theme.spacing.xs,
    fontStyle: 'italic',
  },
  avatarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 70,
    height: 70,
    borderRadius: 35,
  },
  avatarPlaceholder: {
    backgroundColor: theme.colors.primary.light,
    justifyContent: 'center',
    alignItems: 'center',
  },
  changePhotoButton: {
    marginLeft: theme.spacing.lg,
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
  },
  changePhotoText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  userTypeOption: {
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  userTypeOptionSelected: {
    backgroundColor: `${theme.colors.primary.default}15`,
    borderColor: theme.colors.primary.default,
  },
  userTypeOptionText: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.md,
    textAlign: 'center',
  },
  userTypeOptionTextSelected: {
    color: theme.colors.primary.default,
    fontWeight: theme.fontWeights.semibold as any,
  },
  userTypeContainer: {
    flexDirection: 'row',
    flex: 1,
  },
  userTypeTag: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.primary.default,
    backgroundColor: `${theme.colors.primary.default}15`,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
    overflow: 'hidden',
  },
  userTypeTagAdmin: {
    color: theme.colors.util.warning,
    backgroundColor: `${theme.colors.util.warning}15`,
  },
  userTypeTagGestor: {
    color: theme.colors.primary.dark,
    backgroundColor: `${theme.colors.primary.dark}15`,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm,
  },
  inputIcon: {
    marginRight: theme.spacing.sm,
  },
  inputError: {
    borderColor: theme.colors.util.error,
  },
  eyeIcon: {
    padding: theme.spacing.xs,
  },
});

export default UserDetailsScreen; 
